import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { ArchivedZoneNotice } from '@/components/layout/ArchivedZoneNotice';
import { ServerStatusBar } from './ServerStatusBar';
import { ServerDownOverlay } from './ServerDownOverlay';
import { InstallPwaBanner } from '@/components/InstallPwaBanner';
import { PullToRefresh } from '@/components/layout/PullToRefresh';
import { Sheet } from '@/components/ui/sheet';
import { PageHeaderProvider } from '@/contexts/PageHeaderContext';
import { KioskProvider, useKiosk } from '@/contexts/KioskContext';
import { useAutoReloadOnUpdate } from '@/hooks/useAutoReloadOnUpdate';
import { useT } from '@/contexts/I18nContext';
import { useFinanceHidden } from '@/lib/finance';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { Lang } from '@/i18n/dictionaries';

const COLLAPSED_KEY = 'sidebar-collapsed';

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(COLLAPSED_KEY) === '1';
}

export function Layout() {
  // Новый билд → страница перезагружается сама (AI294, планшеты в киоске).
  useAutoReloadOnUpdate();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang } = useT();
  const { user } = useCurrentUser();
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const serverLang = user?.lang;
    if (serverLang && (serverLang === 'ru' || serverLang === 'en') && serverLang !== lang) {
      setLang(serverLang as Lang);
    }
  }, [user?.lang]); // intentionally only reacts to server lang changes

  // Swipe-right (from the left edge) opens the mobile sidebar; swipe-
  // left while it's open closes it. Edge-anchor keeps content with
  // horizontal scroll (carousels, dashboard panels) from triggering
  // an accidental open.
  const touchRef = useRef<{ x: number; y: number; mobileOpen: boolean } | null>(null);
  useEffect(() => {
    function onTouchStart(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      touchRef.current = { x: t.clientX, y: t.clientY, mobileOpen };
    }
    function onTouchEnd(e: TouchEvent) {
      const start = touchRef.current;
      touchRef.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      // Mostly-horizontal swipe (slope < ~30°), > 60px travel.
      if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx) * 0.5) return;
      if (!start.mobileOpen && dx > 0 && start.x < 24) {
        setMobileOpen(true);
      } else if (start.mobileOpen && dx < 0) {
        setMobileOpen(false);
      }
    }
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [mobileOpen]);

  function toggleCollapsed() {
    setCollapsed(prev => {
      const next = !prev;
      try {
        if (next) window.localStorage.setItem(COLLAPSED_KEY, '1');
        else window.localStorage.removeItem(COLLAPSED_KEY);
      } catch {}
      return next;
    });
  }

  function handleLogout() {
    localStorage.removeItem('jwt');
    localStorage.removeItem('refresh');
    localStorage.removeItem('username');
    localStorage.removeItem('me:last');
    localStorage.removeItem('zones:last');
    setMobileOpen(false);
    navigate('/login', { replace: true });
  }

  return (
    <KioskProvider>
      <LayoutShell
        collapsed={collapsed}
        toggleCollapsed={toggleCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        onLogout={handleLogout}
        mainRef={mainRef}
      />
    </KioskProvider>
  );
}

/** Каркас вынесен, чтобы читать KioskContext (#1610: «на весь экран» прячет
 *  сайдбар и шапку — дашборд на весь планшет). */
function LayoutShell({ collapsed, toggleCollapsed, mobileOpen, setMobileOpen, onLogout, mainRef }: {
  collapsed: boolean; toggleCollapsed: () => void; mobileOpen: boolean; setMobileOpen: (v: boolean) => void;
  onLogout: () => void; mainRef: React.RefObject<HTMLElement>;
}) {
  const { kiosk } = useKiosk();
  // Переключили «скрыть финансы» — страница пересобирается, чтобы суммы в memo/состоянии пересчитались.
  const financeHidden = useFinanceHidden();
  const handleLogout = onLogout;
  return (
    <div className="flex h-dvh overflow-hidden bg-background text-foreground">
      <div className={kiosk ? 'hidden' : 'hidden md:flex'}>
        <Sidebar
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          onLogout={handleLogout}
        />
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen} side="left">
        <Sidebar
          variant="mobile"
          collapsed={false}
          onToggleCollapsed={() => {}}
          onNavigate={() => setMobileOpen(false)}
          onLogout={handleLogout}
        />
      </Sheet>

      <PageHeaderProvider>
        <div className="flex flex-1 min-w-0 flex-col">
          {!kiosk && <Header onOpenSidebar={() => setMobileOpen(true)} />}
          {!kiosk && <ArchivedZoneNotice />}
          {/* Одна плашка о связи с сервером за все секции страницы (#3224); в киоске тоже — дашборд на планшете. */}
          <ServerStatusBar />
          {/* Робот поверх страницы, пока сервер лежит (#3260); обёртка не скроллится — робот не уезжает. */}
          <div className="relative flex min-h-0 flex-1 flex-col">
          <ServerDownOverlay />
          <main ref={mainRef} className="relative flex-1 min-h-0 overflow-auto">
            {/* Cross-platform custom pull-to-refresh, active only in the
                installed (standalone) PWA. Overscroll stays fully contained
                (globals.css) so the iOS rubber-band fix isn't reintroduced. */}
            <PullToRefresh scrollRef={mainRef} />
            <React.Fragment key={financeHidden ? 'fin-hidden' : 'fin-shown'}>
              <Outlet />
            </React.Fragment>
          </main>
          </div>
        </div>
        {/* The page name now lives in the header (i) info button on mobile and
            the right-aligned title on desktop — the old bottom-left pill is
            gone (it clashed with the dashboard's bottom tab bar). */}
      </PageHeaderProvider>
      <InstallPwaBanner />
    </div>
  );
}
